import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosSousProfile } from "../../../api/axios";
import { SOUS_PROFILE_PATHS } from "../../../api/endpoints";

const extractErrorMessage = (err, fallback) => {
  const data = err?.response?.data;

  let msg =
    (data &&
    typeof data === "object" &&
    "message" in data
      ? data.message
      : data) ||
    err?.message ||
    fallback;

  if (Array.isArray(msg)) {
    msg = msg.join(", ");
  }

  if (typeof msg !== "string") {
    msg = String(msg);
  }

  return msg;
};

const normalizeId = (value) => {
  if (value == null) return null;

  const id = String(value).trim();

  if (
    !id ||
    ["undefined", "null", "nan"].includes(
      id.toLowerCase()
    )
  ) {
    return null;
  }

  return id;
};

/* ============================================================
   CREATE
============================================================ */

export const createSousProfile = createAsyncThunk(
  "sousProfiles/create",

  async (payload, { rejectWithValue }) => {
    try {
      const res =
        await axiosSousProfile.post(
          SOUS_PROFILE_PATHS.CREATE,
          payload,
          {
            withCredentials: true,
          }
        );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        extractErrorMessage(
          err,
          "Erreur création sous-profil"
        )
      );
    }
  }
);

/* ============================================================
   GET ALL
============================================================ */

export const fetchSousProfiles = createAsyncThunk(
  "sousProfiles/fetchAll",

  async (_, { rejectWithValue }) => {
    try {
      const res =
        await axiosSousProfile.get(
          SOUS_PROFILE_PATHS.GET_ALL,
          {
            withCredentials: true,
          }
        );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        extractErrorMessage(
          err,
          "Erreur récupération sous-profils"
        )
      );
    }
  }
);

/* ============================================================
   GET BY ID
============================================================ */

export const fetchSousProfileById =
  createAsyncThunk(
    "sousProfiles/fetchById",

    async (id, { rejectWithValue }) => {
      try {
        const res =
          await axiosSousProfile.get(
            SOUS_PROFILE_PATHS.GET_BY_ID(id),
            {
              withCredentials: true,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          extractErrorMessage(
            err,
            "Erreur récupération sous-profil"
          )
        );
      }
    }
  );

/* ============================================================
   UPDATE

   BACKEND ATTEND :

   PUT /sous-profiles/:id

   body:
   {
      ownerId,
      ...champs autorisés (firstName, lastName, avatar, avatarUrl, ...)
   }
============================================================ */

export const updateSousProfile =
  createAsyncThunk(
    "sousProfiles/update",

    async (
      { id, ownerId, ...fields },
      { rejectWithValue }
    ) => {
      try {
        const cleanId = normalizeId(id);
        const cleanOwnerId = normalizeId(ownerId);

        if (!cleanId) {
          return rejectWithValue("Subprofile id not found");
        }

        if (!cleanOwnerId) {
          return rejectWithValue("Owner user id not found");
        }

        const res =
          await axiosSousProfile.put(
            SOUS_PROFILE_PATHS.UPDATE(cleanId),

            {
              ownerId: cleanOwnerId,
              ...fields,
            },

            {
              withCredentials: true,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          extractErrorMessage(
            err,
            "Erreur mise à jour sous-profil"
          )
        );
      }
    }
  );

/* ============================================================
   ADD EMAIL
============================================================ */

export const addSousProfileEmail =
  createAsyncThunk(
    "sousProfiles/addEmail",

    async (
      { id, email },
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axiosSousProfile.put(
            SOUS_PROFILE_PATHS.ADD_EMAIL(id),

            {
              email,
            },

            {
              withCredentials: true,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          extractErrorMessage(
            err,
            "Erreur ajout email sous-profil"
          )
        );
      }
    }
  );

/* ============================================================
   DELETE

   BACKEND ATTEND :

   DELETE /sous-profiles/:id

   body:
   {
      ownerId
   }
============================================================ */

export const deleteSousProfile =
  createAsyncThunk(
    "sousProfiles/delete",

    async (
      { id, ownerId },
      { rejectWithValue }
    ) => {
      try {
        const cleanId =
          normalizeId(id);

        const cleanOwnerId =
          normalizeId(ownerId);

        if (!cleanId) {
          return rejectWithValue(
            "Subprofile id not found"
          );
        }

        if (!cleanOwnerId) {
          return rejectWithValue(
            "Owner user id not found"
          );
        }

        const res =
          await axiosSousProfile.delete(
            SOUS_PROFILE_PATHS.DELETE(
              cleanId
            ),

            {
              data: {
                ownerId:
                  cleanOwnerId,
              },

              withCredentials: true,
            }
          );

        return {
          id: cleanId,
          data: res.data,
        };
      } catch (err) {
        return rejectWithValue(
          extractErrorMessage(
            err,
            "Erreur suppression sous-profil"
          )
        );
      }
    }
  );

/* ============================================================
   REQUEST SEPARATION
============================================================ */

export const requestSousProfileSeparation =
  createAsyncThunk(
    "sousProfiles/requestSeparation",

    async (
      id,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axiosSousProfile.post(
            SOUS_PROFILE_PATHS.REQUEST_SEPARATION(
              id
            ),

            {},

            {
              withCredentials: true,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          extractErrorMessage(
            err,
            "Erreur séparation sous-profile"
          )
        );
      }
    }
  );

/* ============================================================
   EMANCIPATION EMAIL
============================================================ */

export const sendEmancipationEmail =
  createAsyncThunk(
    "sousProfiles/sendEmancipationEmail",

    async (
      {
        id,
        email,
      },

      {
        rejectWithValue,
      }
    ) => {
      try {
        await axiosSousProfile.post(
          SOUS_PROFILE_PATHS.REQUEST_SEPARATION(
            id
          ),

          {},

          {
            withCredentials: true,
          }
        );

        const res =
          await axiosSousProfile.put(
            SOUS_PROFILE_PATHS.ADD_EMAIL(id),

            {
              email: String(email)
                .trim()
                .toLowerCase(),
            },

            {
              withCredentials: true,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          extractErrorMessage(
            err,
            "Erreur envoi email émancipation"
          )
        );
      }
    }
  );

/* ============================================================
   GRANT ACCESS

   BACKEND ATTEND ownerId
============================================================ */

export const grantSousProfileAccess =
  createAsyncThunk(
    "sousProfiles/grantAccess",

    async (
      {
        id,
        ownerId,
        userId,
        email,
        permission,
      },

      {
        rejectWithValue,
      }
    ) => {
      try {
        const cleanId =
          normalizeId(id);

        const cleanOwnerId =
          normalizeId(ownerId);

        if (!cleanId) {
          return rejectWithValue(
            "Subprofile id not found"
          );
        }

        if (!cleanOwnerId) {
          return rejectWithValue(
            "Owner user id not found"
          );
        }

        const payload = {
          ownerId:
            cleanOwnerId,
        };

        if (userId) {
          payload.userId =
            userId;
        }

        if (email) {
          payload.email =
            String(email)
              .trim()
              .toLowerCase();
        }

        if (permission) {
          payload.permission =
            permission;
        }

        const res =
          await axiosSousProfile.post(
            SOUS_PROFILE_PATHS.GRANT_ACCESS(
              cleanId
            ),

            payload,

            {
              withCredentials: true,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          extractErrorMessage(
            err,
            "Erreur grant access"
          )
        );
      }
    }
  );

/* ============================================================
   REVOKE ACCESS

   BACKEND ATTEND :

   {
     ownerId,
     userId
   }
============================================================ */

export const revokeSousProfileAccess =
  createAsyncThunk(
    "sousProfiles/revokeAccess",

    async (
      {
        id,
        ownerId,
        userId,
      },

      {
        rejectWithValue,
      }
    ) => {
      try {
        const cleanId =
          normalizeId(id);

        const cleanOwnerId =
          normalizeId(ownerId);

        if (!cleanId) {
          return rejectWithValue(
            "Subprofile id not found"
          );
        }

        if (!cleanOwnerId) {
          return rejectWithValue(
            "Owner user id not found"
          );
        }

        if (!userId) {
          return rejectWithValue(
            "Guardian user id not found"
          );
        }

        const res =
          await axiosSousProfile.post(
            SOUS_PROFILE_PATHS.REVOKE_ACCESS(
              cleanId
            ),

            {
              ownerId:
                cleanOwnerId,

              userId,
            },

            {
              withCredentials: true,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          extractErrorMessage(
            err,
            "Erreur revoke access"
          )
        );
      }
    }
  );

/* ============================================================
   VERIFY TOKEN
============================================================ */

export const verifyEmancipationToken =
  createAsyncThunk(
    "sousProfiles/verifyToken",

    async (
      token,
      {
        rejectWithValue,
      }
    ) => {
      try {
        const res =
          await axiosSousProfile.get(
            SOUS_PROFILE_PATHS.VERIFY_TOKEN,

            {
              params: {
                token,
              },

              withCredentials: true,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          extractErrorMessage(
            err,
            "Erreur vérification token"
          )
        );
      }
    }
  );

/* ============================================================
   SET PASSWORD
============================================================ */

export const setEmancipationPassword =
  createAsyncThunk(
    "sousProfiles/setPassword",

    async (
      {
        token,
        password,
      },

      {
        rejectWithValue,
      }
    ) => {
      try {
        const res =
          await axiosSousProfile.post(
            SOUS_PROFILE_PATHS.SET_PASSWORD,

            {
              token,
              password,
            },

            {
              withCredentials: true,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          extractErrorMessage(
            err,
            "Erreur définition mot de passe"
          )
        );
      }
    }
  );

/* ============================================================
   SWITCH PROFILE

   BACKEND :

   {
      subprofile_id,
      ownerId
   }
============================================================ */

export const switchSousProfile =
  createAsyncThunk(
    "sousProfiles/switchProfile",

    async (
      {
        subprofile_id,
        ownerId,
      },

      {
        rejectWithValue,
      }
    ) => {
      try {
        const cleanSubprofileId =
          normalizeId(
            subprofile_id
          );

        const cleanOwnerId =
          normalizeId(
            ownerId
          );

        if (!cleanSubprofileId) {
          return rejectWithValue(
            "Subprofile id not found"
          );
        }

        if (!cleanOwnerId) {
          return rejectWithValue(
            "Owner user id not found"
          );
        }

        const res =
          await axiosSousProfile.post(
            SOUS_PROFILE_PATHS.SWITCH_PROFILE,

            {
              subprofile_id:
                cleanSubprofileId,

              ownerId:
                cleanOwnerId,
            },

            {
              withCredentials: true,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          extractErrorMessage(
            err,
            "Erreur switch profile"
          )
        );
      }
    }
  );

/* ============================================================
   RESTORE MAIN PROFILE

   BACKEND :

   {
      ownerId
   }
============================================================ */

export const restoreMainProfile =
  createAsyncThunk(
    "sousProfiles/restoreMainProfile",

    async (
      arg = {},
      {
        rejectWithValue,
      }
    ) => {
      try {
        const cleanOwnerId =
          normalizeId(
            arg?.ownerId
          );

        if (!cleanOwnerId) {
          return rejectWithValue(
            "Owner user id not found"
          );
        }

        const res =
          await axiosSousProfile.post(
            SOUS_PROFILE_PATHS.RESTORE,

            {
              ownerId:
                cleanOwnerId,
            },

            {
              withCredentials: true,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          extractErrorMessage(
            err,
            "Erreur restore profile"
          )
        );
      }
    }
  );